import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, AuthResponse } from '../types';
import { setLogoutFunction } from '../services/api';

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  login: (authResponse: AuthResponse) => void;
  logout: () => void;
  updateUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Initialiser l'état avec les données du localStorage
  const storedUser = localStorage.getItem('user');
  const storedToken = localStorage.getItem('token');
  
  const [user, setUser] = useState<User | null>(storedUser ? JSON.parse(storedUser) : null);
  const [token, setToken] = useState<string | null>(storedToken);

  // Fonction de déconnexion qui sera partagée avec l'intercepteur API
  const handleLogout = () => {
    
    // Nettoyer le localStorage d'abord
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    
    // Ensuite mettre à jour l'état du contexte
    setUser(null);
    setToken(null);

    // Rediriger vers la page de connexion
    window.location.href = '/login';
  };

  // Enregistrer la fonction de déconnexion pour l'intercepteur API
  useEffect(() => {
    setLogoutFunction(handleLogout);
  }, []);

  const login = (authResponse: AuthResponse) => {
    setUser(authResponse.user);
    setToken(authResponse.token);
    
    // Stocker dans le localStorage pour la persistance
    localStorage.setItem('user', JSON.stringify(authResponse.user));
    localStorage.setItem('token', authResponse.token);
  };

  const logout = () => {
    // Utiliser la fonction handleLogout pour assurer la cohérence
    handleLogout();
  };

  const updateUser = (updatedUser: User) => {
    setUser(updatedUser);
    
    // Mettre à jour le localStorage
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const value = {
    user,
    token,
    isAuthenticated: !!user && !!token,
    login,
    logout,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth doit être utilisé à l\'intérieur d\'un AuthProvider');
  }
  return context;
};
